import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '@/ui';
import styles from './LoginFormCard.module.css';

export interface LoginFormCardProps {
  readonly username: string;
  readonly onUsernameChange: (value: string) => void;
  readonly password: string;
  readonly onPasswordChange: (value: string) => void;
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  readonly isPending: boolean;
  readonly errorMessage: string | null;
  readonly reasonNotice: string | null;
  readonly onRequestAccess: () => void;
}

export function LoginFormCard({
  username,
  onUsernameChange,
  password,
  onPasswordChange,
  onSubmit,
  isPending,
  errorMessage,
  reasonNotice,
  onRequestAccess,
}: LoginFormCardProps) {
  const [showPassword, setShowPassword] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // AUTOFILL DOES NOT ALWAYS REACH REACT.
  //
  // These are controlled inputs, so the component believes whatever is in
  // state. A browser or password manager that fills them writes straight to
  // the DOM and does not reliably fire the synthetic change event React
  // listens for -- so the fields LOOK filled while state is still empty.
  //
  // Two things went wrong because of that. The submit button, gated on state,
  // stayed disabled until a click or a keystroke happened to sync it, which
  // is why signing in took two clicks. Worse, `signIn({ username, password })`
  // reads the same state, so a submit that did get through would have sent
  // empty credentials.
  //
  // So the DOM is read back after mount. Twice: once immediately, and once
  // after a beat, because Chrome and Edge fill some fields slightly after
  // first paint.
  useEffect(() => {
    const sync = () => {
      const filledUser = usernameRef.current?.value ?? '';
      const filledPass = passwordRef.current?.value ?? '';
      if (filledUser) onUsernameChange(filledUser);
      if (filledPass) onPasswordChange(filledPass);
    };
    sync();
    const timer = window.setTimeout(sync, 250);
    return () => window.clearTimeout(timer);
  }, [onUsernameChange, onPasswordChange]);

  return (
    <div className={styles.formContainer}>
      <div className={styles.header}>
        <span className={styles.eyebrow}>Welcome back</span>
        <h2 className={styles.title}>Sign in to your account</h2>
        <p className={styles.subtitle}>Enter your details below to access your workspace.</p>
      </div>

      {reasonNotice !== null && !errorMessage && (
        <div className={styles.alertNotice}>{reasonNotice}</div>
      )}

      {errorMessage !== null && (
        <div className={styles.alertError} role="alert">
          {errorMessage}
        </div>
      )}

      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {/* Email address field */}
        <div className={styles.fieldGroup}>
          <label htmlFor="email" className={styles.label}>
            Email address
            <span className={styles.srOnly}> (username or email)</span>
          </label>
          <div className={styles.inputWrapper}>
            <Mail size={18} className={styles.inputIcon} aria-hidden="true" />
            <input
              ref={usernameRef}
              id="email"
              type="text"
              name="username"
              className={styles.input}
              placeholder="you@company.com"
              value={username}
              onChange={(e) => onUsernameChange(e.target.value)}
              autoComplete="username"
              required
              disabled={isPending}
            />
          </div>
        </div>

        {/* Password field */}
        <div className={styles.fieldGroup}>
          <label htmlFor="password" className={styles.label}>
            Password
          </label>
          <div className={styles.inputWrapper}>
            <Lock size={18} className={styles.inputIcon} aria-hidden="true" />
            <input
              ref={passwordRef}
              id="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              className={`${styles.input} ${styles.inputWithToggle}`}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              autoComplete="current-password"
              required
              disabled={isPending}
            />
            <button
              type="button"
              className={styles.toggleShowBtn}
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff size={18} aria-hidden="true" />
              ) : (
                <Eye size={18} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Forgot password link */}
        <div className={styles.forgotRow}>
          <Link to="/forgot-password" className={styles.forgotLink}>
            Forgot password?
          </Link>
        </div>

        {/* Primary submit button */}
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={isPending}
          aria-busy={isPending || undefined}
          aria-label="Sign in"
        >
          {isPending ? (
            <>
              <Spinner size={16} />
              <span>Signing in…</span>
            </>
          ) : (
            <>
              <span>Sign in</span>
              <span aria-hidden="true" className={styles.arrowIcon}>
                &rarr;
              </span>
            </>
          )}
        </button>

        {/* Create account / Request access link */}
        <div className={styles.createAccountRow}>
          <span>Don't have an account?</span>{' '}
          <button
            type="button"
            className={styles.createAccountBtn}
            onClick={onRequestAccess}
          >
            Create one
          </button>
        </div>
      </form>

      {/* Legal & Help Footer */}
      <div className={styles.legalFooter}>
        <a href="#privacy" className={styles.legalLink}>
          Privacy Policy
        </a>
        <span className={styles.legalDot}>&bull;</span>
        <a href="#terms" className={styles.legalLink}>
          Terms of Service
        </a>
        <span className={styles.legalDot}>&bull;</span>
        <a href="#help" className={styles.legalLink}>
          Help
        </a>
      </div>
    </div>
  );
}
